import React, { useState } from "react";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * MatchReasonRewriter — small button + expandable form under the AI match
 * reason on an investor-facing startup detail. Investors can add extra
 * context (e.g. "we just closed a fund; prefer sector-adjacent") and pick
 * a tone; one click regenerates via Claude.
 */
export default function MatchReasonRewriter({ startupId, initialReason, onNew }) {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState("");
  const [tone, setTone] = useState("");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(initialReason || null);

  const regen = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/marketplace/startup/${startupId}/match-reason/regenerate`, {
        extra_context: ctx || null,
        tone: tone || null,
      });
      setReason(res.data.match_reason);
      onNew?.(res.data.match_reason);
      toast.success("Match reason regenerated.");
    } catch (e) { toast.error("Regenerate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-gold/40 bg-gold/[0.03] p-5 mb-8" data-testid="ai-match-reason">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="text-gold" size={14} />
          <div className="overline">AI · Match Reasoning</div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-800"
          data-testid="match-reason-rewrite-toggle"
        >
          <RefreshCw size={11} /> {open ? "Hide" : "Rewrite"}
        </button>
      </div>
      <div className="text-sm text-ink-soft leading-relaxed" data-testid="match-reason-body">
        {reason || "—"}
      </div>

      {open && (
        <div className="mt-4 p-3 bg-white rounded border border-navy/10">
          <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Extra context (optional)
          </label>
          <textarea
            value={ctx}
            onChange={(e) => setCtx(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 border border-navy/15 rounded text-xs text-ink resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400"
            rows={3}
            placeholder="e.g. We just closed a fund and prefer sector-adjacent B2B SaaS."
            data-testid="match-reason-context"
            maxLength={500}
          />
          <div className="flex items-center gap-2 mt-2">
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="px-2 py-1 border border-navy/15 rounded text-xs text-ink focus:outline-none focus:ring-1 focus:ring-emerald-400"
              data-testid="match-reason-tone"
            >
              <option value="">Default</option>
              <option value="analyst">Analyst — numbers-first</option>
              <option value="warm">Warm — founder voice</option>
              <option value="sceptical">Sceptical — risks first</option>
            </select>
            <button
              onClick={regen}
              disabled={busy}
              className="ml-auto px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="match-reason-regenerate"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              {busy ? "Regenerating…" : "Regenerate"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
